import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Stack,
  Chip
} from '@mui/material';
import { 
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: Math.random().toString(36).substring(2, 15)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('🚨 Error Boundary Caught:', error);
    console.error('📍 Error Info:', errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Optional: Send to error reporting service
    // reportError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback 
        error={this.state.error}
        errorInfo={this.state.errorInfo}
        errorId={this.state.errorId}
        resetError={() => this.setState({ hasError: false, error: null, errorInfo: null })}
      />;
    }

    return this.props.children;
  }
}

function ErrorFallback({ error, errorInfo, errorId, resetError }) {
  const navigate = useNavigate();

  const handleRefresh = () => {
    resetError();
    window.location.reload();
  };

  const handleGoHome = () => {
    resetError();
    navigate('/');
  };

  const errorType = error?.name || 'Unknown Error';
  const errorMessage = error?.message || 'An unexpected error occurred';
  
  // Determine error severity and styling
  const isModuleError = errorMessage.includes('Failed to fetch dynamically imported module');
  const isSyntaxError = errorMessage.includes('Unexpected token');
  const isNetworkError = errorMessage.includes('net::ERR_');

  const getErrorCategory = () => {
    if (isModuleError || isSyntaxError) return { severity: 'error', category: 'Build Error' };
    if (isNetworkError) return { severity: 'warning', category: 'Network Error' };
    return { severity: 'error', category: 'Runtime Error' };
  };

  const { severity, category } = getErrorCategory();

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3
    }}>
      <Card sx={{
        maxWidth: 600,
        width: '100%',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: severity === 'error' ? 'error.light' : 'warning.light',
        boxShadow: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ErrorIcon 
              color={severity} 
              sx={{ fontSize: 32, mr: 2 }} 
            />
            <Box>
              <Typography variant="h5" color="text.primary" gutterBottom>
                Oops! Something went wrong
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip 
                  label={category} 
                  size="small" 
                  color={severity}
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                  Error ID: {errorId}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Error Details */}
          <Alert severity={severity} sx={{ mb: 3 }}>
            <AlertTitle>{errorType}</AlertTitle>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {errorMessage}
            </Typography>
          </Alert>

          {/* Helpful Messages */}
          {isModuleError && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Module Loading Issue</AlertTitle>
              This usually happens when there's a syntax error in the code or the development server needs to restart.
            </Alert>
          )}

          {isSyntaxError && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Syntax Error Detected</AlertTitle>
              There's a code syntax error that needs to be fixed. The development server will automatically reload once it's resolved.
            </Alert>
          )}

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
            >
              Refresh Page
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
            >
              Go Home
            </Button>
          </Stack>

          {/* Developer Info (Development Only) */}
          {process.env.NODE_ENV === 'development' && error && (
            <Box sx={{ mt: 4, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <BugIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                Developer Details
              </Typography>
              <Typography 
                variant="body2" 
                component="pre" 
                sx={{ 
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 200,
                  overflow: 'auto'
                }}
              >
                {error.stack}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ErrorBoundary;
